import type { RootState } from "@/app/store";
import { TransactionActionFeatureToggles } from "@/modules/transaction/common/enum";
import type { ITransaction } from "@/modules/transaction/common/types";
import { DATE_FORMAT } from "@/shared/common/constants";
import {
  CloudflareAsset,
  OperationCommand,
  OperationStatus,
  OperationStatusColor
} from "@/shared/common/enums";
import type { IActionsMenuAction } from "@/shared/common/types";
import { ActionsMenu, CloudFlareImage } from "@/shared/components";
import { formatDate } from "@/shared/helpers";
import { useAppSelector, useMediaQuery } from "@/shared/hooks";
import type { ICellProps } from "@ab.uitools/ui-library/components/AdvancedTable/types";
import { Button } from "@ab.uitools/ui-library/components/Button";
import { Chips } from "@ab.uitools/ui-library/components/Chips";
import type { ISVGIconProps } from "@ab.uitools/ui-library/components/SVGIcons/types";
import { Text } from "@ab.uitools/ui-library/components/Text";
import { Tooltip } from "@ab.uitools/ui-library/components/Tooltip";
import { Positions } from "@ab.uitools/ui-library/components/Tooltip/types";
import { useTranslation } from "react-i18next";

interface Props {
  onSign: (transaction: ITransaction, index: string) => void;
  rowId?: string | null;
  openMenu: (transaction: ITransaction, index: string) => void;
  isOpenMenu: boolean;
  closeMenu: () => void;
  actions: IActionsMenuAction[];
}

export const useTransactionColumns = ({
  rowId,
  onSign,
  openMenu,
  closeMenu,
  actions,
  isOpenMenu,
}: Props) => {
  const { t } = useTranslation();
  const isDesktop = useMediaQuery();
  const { toggles } = useAppSelector((state: RootState) => state.featureToggle);
  return [
    {
      id: "creationDate",
      header: t("transactionHistoryTable.creationDate"),
      accessorKey: "creationDate",
      enableHiding: false,
      cell: ({ row }: ICellProps<ITransaction>) => {
        const date = row.original?.creationDate;
        return (
          <Text>
            {formatDate(date, DATE_FORMAT.DD_MM_YYYY)}
          </Text>
        );
      },
    },
    {
      id: "status",
      header: t("transactionHistoryTable.status"),
      accessorKey: "state.transactionStatus",
      enableHiding: false,
      cell: ({ row }: ICellProps<ITransaction>) => {
        const status = row.original?.state?.transactionStatus;
        return (
          <div>
            <Chips
              type="accent"
              color={OperationStatusColor[status]}
              text={t(`documentStatus.${OperationStatus[status]}`)}
            />
          </div>
        );
      },
    },
    {
      id: "transaction_number",
      header: t("transactionHistoryTable.transactionNumber"),
      accessorKey: "transactionNumber",
      enableHiding: false,
    },
    {
      id: "transaction_type",
      header: t("transactionHistoryTable.transactionType"),
      accessorKey: "transactionType",
      enableHiding: false,
      cell: ({ row }: ICellProps<ITransaction>) => {
        const transactionType = row.original?.transactionType;
        return (
          <Text>{t(`transactionTypes.${transactionType}`)}</Text>
        );
      },
    },
    {
      id: "account_card",
      header: t("transactionHistoryTable.accountCard"),
      accessorKey: "debitAccount.number",
      enableHiding: false,
    },
    {
      id: "amount",
      header: t("transactionHistoryTable.amount"),
      accessorKey: "amount",
      enableHiding: false,
      cell: ({ row }: ICellProps<ITransaction>) => {
        const amount = row.original?.amount;
        const currency = row.original?.currency;
        return (
          <Text>{`${amount} ${currency}`}</Text>
        );
      },
    },
    {
      id: "beneficiary",
      header: t("transactionHistoryTable.beneficiary"),
      accessorKey: "beneficiary",
    },
    {
      id: "purpose",
      header: t("transactionHistoryTable.purpose"),
      accessorKey: "purpose",
    },
    {
      id: "actions",
      header: t("transactionHistoryTable.actions"),
      accessorKey: "actions",
      maxSize: 100,
      cell: ({ row }: ICellProps<ITransaction>) => {
        const isSignable = row.original.state?.transactionCommand?.includes(
          OperationCommand.Sign
        );

        const filteredActions = actions.filter((action) => {
          const transactionType = row.original?.transactionType;
          const stateCommands = row.original?.state?.transactionCommand || [];

          const copyAvailableKey = `${transactionType}Copy` as keyof typeof TransactionActionFeatureToggles;
          const editAvailableKey = `${transactionType}Edit` as keyof typeof TransactionActionFeatureToggles;

          const isCopyAvailable = toggles?.[TransactionActionFeatureToggles[copyAvailableKey]];
          const isEditAvailable = toggles?.[TransactionActionFeatureToggles[editAvailableKey]];

          if (!stateCommands.includes(action.value as string)) return false;

          if (action.value === "Copy") return !!isCopyAvailable;
          if (action.value === "Edit") return !!isEditAvailable;

          return true;
        }).map((action) => ({
          ...action,
          handler: () => action.handler?.({
            operationId: row.original?.id,
            operationType: row.original?.transactionType,
          })
        }));

        return (
          <div className="flexbox align-items--center gap-8">
            <Tooltip
              position={Positions.MIDDLE_LEFT}
              id={`${row.id}_sign-icon-button`}
              text={t("general.sign")}
            />
            {isSignable && (
              <Button
                onClick={() => onSign(row.original, row.id)}
                type="primary"
                size={isDesktop ? "large" : "medium"}
                id={`${row.id}_sign-icon-button`}
                data-testid="transaction-sign"
                data-ga-id="transaction-sign"
                data-ga-action="Transfers history table sign button"
                iconProps={{
                  Component: (props: ISVGIconProps) => (
                    <CloudFlareImage {...props} path={CloudflareAsset.SignLight}/>
                  ),
                }}
              />
            )}
            {!!filteredActions.length && (
              <ActionsMenu
                isOpen={row.original.id === rowId && isOpenMenu}
                actions={filteredActions}
                parentClass="advanced-table__inner"
                openMenu={() => openMenu(row.original, row.id)}
                isInTable
                position={Positions.BOTTOM_RIGHT}
                closeMenu={closeMenu}
              />
            )}
          </div>
        );
      },
    },
  ];
};
